import type { InferenceMode, ResponseMode } from "../types";

export const NEXUS_PROPRIETARY_TECH_ID = "NEXUS-AIR-v1" as const;
export const NEXUS_LAYER_STREAM_TECH_ID = "NEXUS-LayerStream-v1" as const;

export type NexusRoute = "server-hosted" | "friend-consent-p2p" | "world-opt-in-p2p";
export type NexusFeatureState = "active" | "guarded" | "projected" | "not-connected";

export interface NexusFeatureMatrix {
  typingTimePrefill: NexusFeatureState;
  speculativePipeline: NexusFeatureState;
  rttAwarePlacement: NexusFeatureState;
  activationDeltaCoding: NexusFeatureState;
  layerStreamDelivery: NexusFeatureState;
}

export interface NexusInferencePlan {
  technologyId: typeof NEXUS_PROPRIETARY_TECH_ID;
  route: NexusRoute;
  routeLabel: string;
  targetPeerCount: number;
  availablePeerCount: number;
  consentRequired: boolean;
  keepsModelOffUserDevice: boolean;
  ledgerMode: "async-encrypted-ledger";
  realtimeControl: "durable-object-websocket";
  payloadPath: "server-relay" | "webrtc-datachannel";
  featureMatrix: NexusFeatureMatrix;
  layerStream: {
    technologyId: typeof NEXUS_LAYER_STREAM_TECH_ID;
    modelDistribution: "server-owned-full-model" | "on-demand-layer-ranges";
    activationPacketProtocol: "nexus-activation-packet-v1";
    fullModelDistributed: false;
  };
  decisionSummary: string;
}

export interface NexusPlanInput {
  inferenceMode: InferenceMode;
  responseMode: ResponseMode;
  availablePeerCount: number;
  inputCharacters: number;
  attachmentCount: number;
}

function targetPeerCount(responseMode: ResponseMode, availablePeerCount: number): number {
  const desired = responseMode === "fast" ? 1 : responseMode === "medium" ? 2 : 3;
  return Math.max(0, Math.min(desired, availablePeerCount));
}

export function planNexusInference(input: NexusPlanInput): NexusInferencePlan {
  if (!Number.isSafeInteger(input.availablePeerCount) || input.availablePeerCount < 0) {
    throw new RangeError("availablePeerCount must be a non-negative safe integer");
  }
  if (!Number.isSafeInteger(input.inputCharacters) || input.inputCharacters < 0) {
    throw new RangeError("inputCharacters must be a non-negative safe integer");
  }
  if (!Number.isSafeInteger(input.attachmentCount) || input.attachmentCount < 0) {
    throw new RangeError("attachmentCount must be a non-negative safe integer");
  }

  const distributed = input.inferenceMode !== "local" && input.availablePeerCount > 0;
  const route: NexusRoute = !distributed
    ? "server-hosted"
    : input.inferenceMode === "world"
      ? "world-opt-in-p2p"
      : "friend-consent-p2p";
  const selectedPeers = distributed ? targetPeerCount(input.responseMode, input.availablePeerCount) : 0;

  const featureMatrix: NexusFeatureMatrix = {
    typingTimePrefill: input.inputCharacters >= 12 || input.attachmentCount > 0 ? "guarded" : "projected",
    speculativePipeline: distributed ? "projected" : "not-connected",
    rttAwarePlacement: distributed ? "active" : "guarded",
    activationDeltaCoding: distributed ? "projected" : "not-connected",
    layerStreamDelivery: distributed ? "projected" : "guarded",
  };

  const routeLabel =
    route === "server-hosted"
      ? "親機サーバーモデル"
      : route === "world-opt-in-p2p"
        ? "世界分散 opt-in P2P"
        : "友人分散 consent P2P";

  return {
    technologyId: NEXUS_PROPRIETARY_TECH_ID,
    route,
    routeLabel,
    targetPeerCount: selectedPeers,
    availablePeerCount: input.availablePeerCount,
    consentRequired: distributed,
    keepsModelOffUserDevice: true,
    ledgerMode: "async-encrypted-ledger",
    realtimeControl: "durable-object-websocket",
    payloadPath: distributed ? "webrtc-datachannel" : "server-relay",
    featureMatrix,
    layerStream: {
      technologyId: NEXUS_LAYER_STREAM_TECH_ID,
      modelDistribution: distributed ? "on-demand-layer-ranges" : "server-owned-full-model",
      activationPacketProtocol: "nexus-activation-packet-v1",
      fullModelDistributed: false,
    },
    decisionSummary:
      route === "server-hosted"
        ? "モデル本体を利用者端末へ配布せず、親機側モデルへ安全に中継します。"
        : `${input.availablePeerCount}台の同意済み端末からRTT順に最大${selectedPeers}台を選び、P2P経路を優先します。`,
  };
}
